import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SimpleMarkdown from "@/components/SimpleMarkdown";
import { ExternalLink, FileText, Beaker, Code2, Shield } from "lucide-react";

export default function ApiDocsPage() {
  const [md, setMd] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeHeading, setActiveHeading] = useState("");

  useEffect(() => {
    fetch("/api/v1/publisher-api-v2.md")
      .then(r => r.text())
      .then(t => { setMd(t); setLoading(false); })
      .catch(() => setLoading(false));
    document.title = "Publisher API v2 — Dokumentacja | Epidemos";
  }, []);

  // Generuj TOC z H2
  const toc = useMemo(() => {
    if (!md) return [];
    const lines = md.split("\n");
    const items = [];
    for (const line of lines) {
      const m = line.match(/^##\s+(.+)/);
      if (m) {
        const text = m[1].replace(/[<>"]/g, "");
        const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
        items.push({ id, text });
      }
    }
    return items;
  }, [md]);

  // Scrollspy
  useEffect(() => {
    const onScroll = () => {
      const headings = document.querySelectorAll("h2[id]");
      let current = "";
      for (const h of headings) {
        if (h.getBoundingClientRect().top < 120) current = h.id;
      }
      setActiveHeading(current);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Header onLogin={() => window.location.href = "/login"} />

      {/* Hero */}
      <section className="pt-24 pb-12 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
        <div className="max-w-none mx-auto px-6">
          <div className="flex items-center gap-3 mb-4 text-blue-200">
            <Code2 className="w-5 h-5" />
            <span className="text-sm font-semibold">Dla wydawców portali</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Publisher API v2</h1>
          <p className="text-xl text-blue-100 max-w-none mb-6">
            Jeden standard integracji portalu z agentem Epidemos. Zaimplementuj 6 endpointów,
            przetestuj w sandboxie i podpinaj swój portal w 15 minut.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/wydawcy-testy" className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-blue-700 font-semibold rounded-lg hover:bg-blue-50 transition">
              <Beaker className="w-4 h-4" /> Uruchom sandbox
            </Link>
            <a href="/api/v1/publisher-api-v2.md" target="_blank" rel="noopener"
               className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-700/50 hover:bg-blue-700/80 text-white font-semibold rounded-lg transition border border-blue-300/30">
              <FileText className="w-4 h-4" /> Markdown raw <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div className="mt-8 flex flex-wrap gap-4 text-sm text-blue-100">
            <div className="flex items-center gap-1.5"><Shield className="w-4 h-4" /> Scope'y kluczy</div>
            <div className="flex items-center gap-1.5">📷 Hosting obrazów z dedupe</div>
            <div className="flex items-center gap-1.5">💬 Komentarze AI person</div>
            <div className="flex items-center gap-1.5">⭐ Oceny artykułów</div>
            <div className="flex items-center gap-1.5">🔄 PATCH partial updates</div>
            <div className="flex items-center gap-1.5">🧪 Sandbox z 15 testami</div>
          </div>
        </div>
      </section>

      {/* Content + TOC */}
      <section className="max-w-none mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar TOC */}
          <aside className="hidden lg:block lg:col-span-1">
            <div className="sticky top-24 max-h-[calc(100vh-120px)] overflow-y-auto">
              <h3 className="font-bold text-slate-800 mb-3 text-sm uppercase tracking-wider">Spis treści</h3>
              <nav className="space-y-1 text-sm">
                {toc.map(it => (
                  <a key={it.id} href={`#${it.id}`}
                     className={`block py-1 px-3 rounded transition border-l-2 ${
                       activeHeading === it.id
                         ? "bg-blue-50 text-blue-700 border-blue-500 font-medium"
                         : "text-slate-600 hover:text-slate-900 hover:bg-slate-50 border-transparent"
                     }`}>
                    {it.text.length > 35 ? it.text.substring(0, 35) + "…" : it.text}
                  </a>
                ))}
              </nav>
              <div className="mt-6 p-4 bg-slate-100 rounded-lg">
                <h4 className="font-semibold text-slate-900 text-sm mb-2">Gotowy?</h4>
                <p className="text-xs text-slate-600 mb-3">
                  Przetestuj implementację w sandboxie przed dodaniem do produkcji.
                </p>
                <Link to="/wydawcy-testy"
                      className="block w-full text-center py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded transition">
                  Sandbox →
                </Link>
              </div>
            </div>
          </aside>

          {/* Markdown content */}
          <article className="lg:col-span-3 bg-white rounded-xl shadow-sm border border-slate-200 p-6 sm:p-10">
            {loading ? (
              <div className="py-12 text-center text-slate-500">Ładowanie dokumentacji…</div>
            ) : md ? (
              <SimpleMarkdown source={md} />
            ) : (
              <div className="py-12 text-center text-slate-500">
                Nie udało się załadować dokumentacji. Spróbuj ponownie.
              </div>
            )}
          </article>
        </div>
      </section>
      <Footer />
    </div>
  );
}
